import type { QueryClient } from '@tanstack/react-query';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { getProfessionalsQuery } from '@/api/booking/getProfessionalsQuery';
import {
  type ServiceCategoryRow,
  getServiceCategoriesQuery,
} from '@/api/booking/getServiceCategoriesQuery';
import { getServicesQuery } from '@/api/booking/getServicesQuery';
import type { ShopDetail } from '@/api/shop/shopDetailQuery';
import type { Service } from '@/types/booking';
import { assignUncategorizedToOther } from '@/utils/booking/assignUncategorizedToOther';

interface PeekCategoryDataParams {
  queryClient: QueryClient;
  /** Undefined while the shop is still loading. */
  shop: Pick<ShopDetail, 'id' | 'brandId'> | undefined;
  /** Undefined means "any professional". */
  barberIdOrRoute?: string;
}

interface PeekedCategoryData {
  barberId?: string;
  rows: ServiceCategoryRow[];
  services: Service[];
}

/**
 * Reads the category rows and services of the category lists from the query
 * cache only. Returns null when any entry is not cached, which callers treat
 * as "not known".
 */
export function peekCategoryData({
  queryClient,
  shop,
  barberIdOrRoute,
}: PeekCategoryDataParams): PeekedCategoryData | null {
  if (!shop) return null;

  let barberId: string | undefined;
  if (barberIdOrRoute) {
    const listed = queryClient
      .getQueryData(getProfessionalsQuery(shop.id).queryKey)
      ?.find(
        ({ barber }) =>
          barber.id === barberIdOrRoute || barber.route === barberIdOrRoute,
      );
    const professional =
      listed ??
      queryClient.getQueryData(
        getBarberByIdOrRouteQuery(shop.id, barberIdOrRoute).queryKey,
      );
    if (!professional) return null;
    barberId = professional.barber.id;
  }

  const categories = queryClient.getQueryData(
    getServiceCategoriesQuery({
      brandId: shop.brandId,
      shopId: shop.id,
      barberId,
    }).queryKey,
  );
  if (!categories) return null;

  const services = queryClient.getQueryData(
    getServicesQuery(shop.id, barberId).queryKey,
  )?.services;
  if (!services) return null;

  return {
    barberId,
    rows: categories.rows,
    services: assignUncategorizedToOther(services, categories.rows),
  };
}
